import { createSlice, isAnyOf, PayloadAction } from "@reduxjs/toolkit";
import {
    getCatalogFinGroupsDetailThunk,
    getCatalogFinGroupsThunk,
    getExportExportPaymentsThunk,
    getExportExportPromosThunk,
    getExportExportSchedulesThunk,
    getInvoiceGetSalesBySKUByPointIdsThunk,
    getInvoiceSalesByPointsThunk,
    getKpiGetProgressThunk,
    getKpiGetSalaryToPayThunk,
    getOrderClosedCheckoutListByPointsThunk,
    getOrderOpenedCheckoutListByPointsThunk,
    getPointInfoCollectionThunk,
    getPointInfoThunk,
    getSchedulerDayPointScheduleThunk,
    getSchedulerPointScheduleDetailsThunk,
    getTaskListsThunk,
    getWorkCheckThunk,
    getWorkGetScheduleThunk,
    getWorkStartAndGetScheduleThunk,
    postTaskAddDebtTaskThunk,
    postTaskAddDistributionPricesThunk,
    postTaskAddRejectTaskThunk,
    postTaskAddSaleTaskThunk,
    postWorkStartThunk,
    postWorkStopThunk,
} from "tm-shared/api";
import {
    AnyTask,
    CheckoutOrder,
    PaymentForm,
    Point,
    ProductOrderItemDetails,
    VerifierQrPayloadWithScanMeta,
    WorkStatus,
} from "tm-shared/types";
import { arrayToRecord } from "tm-shared/utils";

import { ContractFormValues } from "@/types/form";

import {
    getOrderOpenedYesterdayCheckoutListThunk,
    getPointInfoCollectionUnplannedThunk,
} from "./work.slice.thunks";
import { WorkState } from "./work.slice.types";
import { createCheckoutOrder, setTask } from "./work.slice.utils";

const initialState: WorkState = {
    points: {},
    taskPointMap: {},
    pointIds: [],
    pointExtraIds: [],
    pointsSales: [],
    unplannedPoints: {},

    tasks: {},
    workStatus: null,
    yesterdayCheckoutList: [],
    checkoutList: {},
    checkoutCompletedList: {},
    mmlStatistics: null,
    mmlStatisticsDate: "",
    mmlStatisticsError: "",
    motivation: null,
    motivationError: "",
    salary: null,
    salaryError: "",
    skuSales: [],
    exportIndicator: 0,
    supervisorCheck: {},
    currentRouteList: [],
    contractFormValues: null,
    options: {
        finGroups: [],
        finGroupsDetail: [],
    },
};

const workSlice = createSlice({
    name: "work",
    initialState,
    reducers: {
        setTaskCompleted: (
            state,
            action: PayloadAction<{
                taskId: string;
                targetId: string;
                value?: boolean;
            }>,
        ) => {
            const { taskId, targetId, value = true } = action.payload;
            if (state.tasks[targetId] && state.tasks[targetId][taskId]) {
                state.tasks[targetId][taskId].completed = value;
            }
        },

        addItemToCheckout: (
            state,
            action: PayloadAction<{
                checkoutList: Record<string, CheckoutOrder>;
            }>,
        ) => {
            state.checkoutList = {
                ...state.checkoutList,
                ...action.payload.checkoutList,
            };
        },

        setCheckoutProducts: (
            state,
            action: PayloadAction<{
                orderId: string;
                products: Record<string, ProductOrderItemDetails>;
            }>,
        ) => {
            const { orderId, products } = action.payload;
            if (!state.checkoutList[orderId]) return;

            state.checkoutList[orderId].products = products;
        },

        addAdditionalOfflineTask: (
            state,
            action: PayloadAction<
                Omit<AnyTask, "info" | "targetDate" | "targetType">
            >,
        ) => {
            const { id: taskId, targetId } = action.payload;

            if (!state.tasks[targetId]) {
                state.tasks[targetId] = {};
            }

            if (!state.tasks[targetId][taskId]) {
                const task = {
                    ...action.payload,
                    targetDate: "",
                    targetType: "market",
                    info: {
                        performer: {
                            userId: "",
                            name: "",
                            role: "",
                            roleSlug: "",
                            phone: "",
                        },
                    },
                } as AnyTask;

                setTask(state, task);
            }
        },
        changeCheckoutForm: (state, action: PayloadAction<string>) => {
            const checkout = state.checkoutList[action.payload];

            if (!checkout) return;

            checkout.paymentForm =
                checkout.paymentForm === PaymentForm.Card1
                    ? PaymentForm.Cash2
                    : PaymentForm.Card1;
        },

        changeCheckoutShipmentDate: (
            state,
            action: PayloadAction<{
                orderId: string;
                date: string;
            }>,
        ) => {
            const checkout = state.checkoutList[action.payload.orderId];

            if (!checkout) return;

            checkout.customShipmentDate = action.payload.date;
        },

        increaseExportIndicator(
            state,
            action: PayloadAction<number | string | undefined>,
        ) {
            const value = action.payload ?? 1;

            if (
                typeof value === "number" &&
                typeof state.exportIndicator === "number"
            ) {
                state.exportIndicator += value;
            } else {
                state.exportIndicator = value;
            }
        },

        decreaseExportIndicator(state) {
            const step = 1;

            if (typeof state.exportIndicator === "number") {
                const next = state.exportIndicator - step;
                state.exportIndicator = next < 0 ? 0 : next;
            } else {
                state.exportIndicator = 0;
            }
        },
        setSupervisorCheck(
            state,
            action: PayloadAction<{
                taskId: string;
                payload: VerifierQrPayloadWithScanMeta;
            }>,
        ) {
            const { taskId, payload } = action.payload;

            state.supervisorCheck[taskId] = payload;
        },
        removeSupervisorCheck(state, action: PayloadAction<string>) {
            const taskId = action.payload;

            delete state.supervisorCheck[taskId];
        },

        setContractFormValues: (
            state,
            action: PayloadAction<ContractFormValues | null>,
        ) => {
            state.contractFormValues = action.payload;
        },
        patchContractFormValues: (
            state,
            action: PayloadAction<Partial<ContractFormValues>>,
        ) => {
            state.contractFormValues = {
                ...state.contractFormValues,
                ...action.payload,
            };
        },

        // Yesterday opened checkouts

        removeYesterdayCheckoutItem: (state, action: PayloadAction<string>) => {
            state.yesterdayCheckoutList = state.yesterdayCheckoutList.filter(
                ({ orderId }) => orderId !== action.payload,
            );
        },
    },
    extraReducers: (builder) => {
        builder

            .addCase(getPointInfoCollectionThunk.fulfilled, (state, action) => {
                state.points = {
                    ...state.points,
                    ...arrayToRecord(action.payload.result, "id"),
                };
            })

            .addCase(getTaskListsThunk.fulfilled, (state, { payload }) => {
                const tasks = payload.result;

                tasks.forEach((task) => {
                    setTask(state, task);
                });
            })

            .addCase(
                getSchedulerDayPointScheduleThunk.fulfilled,
                (state, action) => {
                    state.pointIds = action.payload.result
                        .filter(
                            ({ pointId }) =>
                                pointId &&
                                !state.pointExtraIds?.includes(pointId),
                        )
                        .map(({ pointId }) => pointId);
                },
            )

            .addCase(postWorkStopThunk.fulfilled, (state) => {
                state.workStatus = WorkStatus.EndWork;
            })

            .addCase(postWorkStartThunk.fulfilled, (state) => {
                state.workStatus = WorkStatus.StartWork;
            })

            .addCase(getWorkCheckThunk.fulfilled, (state, action) => {
                state.workStatus = action.payload.result;
            })

            .addCase(
                getOrderOpenedCheckoutListByPointsThunk.fulfilled,
                (state, { payload }) => {
                    state.checkoutList = {};
                    const { result: checkoutOrders } = payload;

                    checkoutOrders.forEach((order) => {
                        if (state.checkoutList[order.orderId]) return;

                        state.checkoutList[order.orderId] = createCheckoutOrder(
                            order,
                            false,
                        );
                    });
                },
            )

            .addCase(
                getOrderClosedCheckoutListByPointsThunk.fulfilled,
                (state, { payload }) => {
                    state.checkoutCompletedList = {};
                    const { result: checkoutOrders } = payload;

                    checkoutOrders.forEach((order) => {
                        state.checkoutCompletedList[order.orderId] =
                            createCheckoutOrder(order, true);
                    });
                },
            )

            .addCase(getKpiGetProgressThunk.fulfilled, (state, action) => {
                state.motivation = action.payload.result;
                state.motivationError = "";
            })

            .addCase(getKpiGetProgressThunk.rejected, (state) => {
                state.motivationError =
                    "План мотивації наразі не розраховано! Для уточнення зверніться до керівника";
                state.motivation = null;
            })

            .addCase(getKpiGetSalaryToPayThunk.fulfilled, (state, action) => {
                state.salary = action.payload.result;
                state.salaryError = "";
            })
            .addCase(getKpiGetSalaryToPayThunk.rejected, (state) => {
                state.salaryError =
                    "Зарплатні дані наразі відсутні! Для уточнення зверніться до керівника";
                state.salary = null;
            })

            .addCase(
                getInvoiceGetSalesBySKUByPointIdsThunk.fulfilled,
                (state, action) => {
                    state.skuSales = action.payload.result;
                },
            )

            .addCase(
                getInvoiceGetSalesBySKUByPointIdsThunk.rejected,
                (state) => {
                    state.skuSales = [];
                },
            )

            .addCase(
                getInvoiceSalesByPointsThunk.fulfilled,
                (state, action) => {
                    state.pointsSales = action.payload.result;
                },
            )
            .addCase(getInvoiceSalesByPointsThunk.rejected, (state) => {
                state.pointsSales = [];
            })

            .addCase(getExportExportSchedulesThunk.pending, (state, action) => {
                if (
                    action.meta.arg.routeIds &&
                    action.meta.arg.routeIds.length === 1 &&
                    typeof state.exportIndicator === "number"
                ) {
                    state.exportIndicator += 1;
                } else {
                    state.exportIndicator = "A";
                }
            })

            .addCase(
                getSchedulerPointScheduleDetailsThunk.fulfilled,
                (state, { payload }) => {
                    state.currentRouteList = payload.result;
                },
            )

            .addCase(
                getPointInfoCollectionUnplannedThunk.fulfilled,
                (state, { payload }) => {
                    state.unplannedPoints = arrayToRecord(payload.result, "id");
                },
            )

            .addCase(getCatalogFinGroupsThunk.fulfilled, (state, action) => {
                state.options.finGroups = action.payload.result.map((item) => ({
                    value: item,
                    label: item,
                }));
            })
            .addCase(
                getCatalogFinGroupsDetailThunk.fulfilled,
                (state, action) => {
                    state.options.finGroupsDetail = action.payload.result.map(
                        (item) => ({
                            value: item,
                            label: item,
                        }),
                    );
                },
            )
            .addCase(
                getOrderOpenedYesterdayCheckoutListThunk.fulfilled,
                (state, { payload }) => {
                    state.yesterdayCheckoutList = payload.result;
                },
            )
            .addCase(getPointInfoThunk.fulfilled, (state, { payload }) => {
                const point = payload.result;

                state.points[point.id] = point;
            })

            .addCase(getWorkStartAndGetScheduleThunk.fulfilled, (state) => {
                state.workStatus = WorkStatus.StartWork;
            })

            .addMatcher(
                isAnyOf(
                    getWorkGetScheduleThunk.fulfilled,
                    getWorkStartAndGetScheduleThunk.fulfilled,
                ),
                (state, action) => {
                    const { tasks, dayPointScheduleExtra, points } =
                        action.payload.result;

                    state.tasks = {};
                    tasks.forEach((task) => {
                        setTask(state, task);
                    });

                    const extraIdsSet = new Set(dayPointScheduleExtra);
                    state.pointExtraIds = dayPointScheduleExtra;

                    const pointsIds: string[] = [];
                    const pointsMap: Record<string, Point> = {};

                    for (const [id, point] of Object.entries(points)) {
                        const isUnplanned = extraIdsSet.has(id);

                        if (!isUnplanned) {
                            pointsIds.push(id);
                        }

                        pointsMap[id] = {
                            ...point,
                            unplanned: isUnplanned,
                        };
                    }

                    state.pointIds = pointsIds;
                    state.points = pointsMap;
                },
            )

            .addMatcher(
                isAnyOf(
                    postTaskAddDebtTaskThunk.fulfilled,
                    postTaskAddDistributionPricesThunk.fulfilled,
                    postTaskAddSaleTaskThunk.fulfilled,
                    postTaskAddRejectTaskThunk.fulfilled,
                ),
                (state, { payload }) => {
                    if (!payload.result) return;

                    setTask(state, payload.result);
                },
            )
            .addMatcher(
                isAnyOf(
                    getExportExportPromosThunk.pending,
                    getExportExportPaymentsThunk.pending,
                ),
                (state) => {
                    state.exportIndicator = "P";
                },
            )

            .addMatcher(
                isAnyOf(
                    getExportExportSchedulesThunk.rejected,
                    getExportExportPromosThunk.rejected,
                    getExportExportPaymentsThunk.rejected,
                ),
                (state) => {
                    if (typeof state.exportIndicator === "string") {
                        state.exportIndicator = 0;
                    } else {
                        state.exportIndicator -= 1;
                    }
                },
            );
    },
});

export const {
    setTaskCompleted,
    addItemToCheckout,
    addAdditionalOfflineTask,
    changeCheckoutForm,
    setCheckoutProducts,
    increaseExportIndicator,
    decreaseExportIndicator,
    changeCheckoutShipmentDate,
    setSupervisorCheck,
    removeSupervisorCheck,
    setContractFormValues,
    patchContractFormValues,
    removeYesterdayCheckoutItem,
} = workSlice.actions;

export default workSlice.reducer;
